import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, ExternalLink, RefreshCw, Save, Sparkles, X } from 'lucide-react'
import { outreachService } from '../../services/outreachService'
import DeliveryPanel from './DeliveryPanel'

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—'
  return `${Math.round(Number(value)).toLocaleString('cs-CZ')} Kč`
}

const formatConfidence = (value) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—'
  return `${Math.round(Number(value) * 100)} %`
}

const statusDetails = {
  generated: { label: 'Vygenerovaný', className: 'bg-violet-100 text-violet-800' },
  edited: { label: 'Ručně upravený', className: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Schválený', className: 'bg-emerald-100 text-emerald-800' },
  sent: { label: 'Odeslaný', className: 'bg-blue-100 text-blue-800' },
  stale: { label: 'Zastaralý podklad', className: 'bg-amber-100 text-amber-800' },
}

const EvidenceScenario = ({ label, scenario }) => {
  const summary = scenario?.summary
  if (!summary) return null

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-lg font-semibold text-slate-950">{formatPrice(summary.anchorPrice)}</div>
      <div className="mt-1 text-xs text-slate-500">
        {formatPrice(summary.anchorPpm2)}/m² · důvěra {formatConfidence(summary.confidence)}
      </div>
      {summary.compsUsed !== null && summary.compsUsed !== undefined && (
        <div className="mt-1 text-xs text-slate-400">Použito srovnání: {summary.compsUsed}</div>
      )}
    </div>
  )
}

const DraftWorkspace = ({ contact, onClose, onContactUpdated }) => {
  const [subject, setSubject] = useState(contact.draft?.subject || '')
  const [body, setBody] = useState(contact.draft?.body || '')
  const [operation, setOperation] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setSubject(contact.draft?.subject || '')
    setBody(contact.draft?.body || '')
  }, [contact._id, contact.draft?.revision, contact.draft?.subject, contact.draft?.body])

  const draft = contact.draft
  const valuation = contact.research?.valuation
  const currentSummary = valuation?.scenarios?.current?.summary
  const hasEvidence = Boolean(
    contact.research?.listing &&
    Number(currentSummary?.anchorPrice) > 0 &&
    Number(currentSummary?.compsUsed) > 0
  )
  const dirty = Boolean(draft) && (subject !== draft.subject || body !== draft.body)
  const validContent = subject.trim().length > 0 && subject.trim().length <= 120 && body.trim().length > 0 && body.trim().length <= 3000
  const status = statusDetails[draft?.status]
  const busy = Boolean(operation)

  const claims = useMemo(() => draft?.factualClaims || [], [draft?.factualClaims])

  const updateContact = (updatedContact) => {
    onContactUpdated(updatedContact)
    setSubject(updatedContact.draft?.subject || '')
    setBody(updatedContact.draft?.body || '')
  }

  const handleGenerate = async () => {
    try {
      setOperation('generate')
      setError('')
      updateContact(await outreachService.generateDraft(contact._id))
    } catch (requestError) {
      console.error('Unable to generate outreach draft:', requestError)
      setError(requestError.message || 'Návrh se nepodařilo vytvořit.')
    } finally {
      setOperation(null)
    }
  }

  const handleSave = async () => {
    if (!draft || !validContent) return
    try {
      setOperation('save')
      setError('')
      updateContact(await outreachService.saveDraft(contact._id, {
        subject,
        body,
        revision: draft.revision,
      }))
    } catch (requestError) {
      console.error('Unable to save outreach draft:', requestError)
      setError(requestError.message || 'Návrh se nepodařilo uložit.')
    } finally {
      setOperation(null)
    }
  }

  const handleApprove = async () => {
    if (!draft || dirty || !validContent || draft.status === 'stale' || draft.status === 'sent') return
    try {
      setOperation('approve')
      setError('')
      updateContact(await outreachService.approveDraft(contact._id, draft.revision))
    } catch (requestError) {
      console.error('Unable to approve outreach draft:', requestError)
      setError(requestError.message || 'Návrh se nepodařilo schválit.')
    } finally {
      setOperation(null)
    }
  }

  const handleClose = useCallback(() => {
    if (!dirty || window.confirm('Zavřít bez uložení změn?')) onClose()
  }, [dirty, onClose])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6" role="dialog" aria-modal="true" aria-labelledby="outreach-draft-title">
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="outreach-draft-title" className="text-lg font-semibold text-slate-950">Návrh individuálního e-mailu</h2>
              {status && <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}>{status.label}</span>}
            </div>
            <p className="mt-1 truncate text-sm text-slate-500">{contact.email}</p>
          </div>
          <button type="button" onClick={handleClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Zavřít">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[360px_minmax(0,1fr)] lg:overflow-hidden">
          <aside className="space-y-4 border-b border-slate-200 bg-slate-50 p-5 lg:overflow-y-auto lg:border-b-0 lg:border-r sm:p-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-700">Důkazní podklad</div>
              <h3 className="mt-2 font-semibold text-slate-950">{contact.research?.listing?.title || 'Inzerát není připraven'}</h3>
              <p className="mt-1 text-sm text-slate-600">{contact.research?.listing?.location || 'Lokalita neuvedena'}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-700">Nabídka {formatPrice(contact.research?.listing?.price)}</span>
                {contact.research?.listing?.listingUrl && (
                  <a href={contact.research.listing.listingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-medium text-violet-700 hover:underline">
                    Otevřít inzerát <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <EvidenceScenario label="Odhad v současném stavu" scenario={valuation?.scenarios?.current} />
              {!hasEvidence && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Odhad současného stavu ještě není připravený. Zavřete okno, použijte Obnovit a návrh vytvořte po jeho načtení.
                </div>
              )}
            </div>

            {draft?.angle && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">Zvolený úhel</div>
                <p className="mt-1 text-sm leading-5 text-violet-950">{draft.angle}</p>
              </div>
            )}

            {claims.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Deklarovaná fakta AI</div>
                <div className="mt-2 space-y-2">
                  {claims.map((item, index) => (
                    <div key={`${item.evidenceKey}-${index}`} className="rounded-lg bg-white p-2.5 text-xs text-slate-600">
                      <div>{item.claim}</div>
                      <div className="mt-1 font-mono text-[10px] text-slate-400">{item.evidenceKey}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <main className="flex min-h-[560px] flex-col p-5 lg:overflow-y-auto sm:p-6">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!draft ? (
              <div className="flex flex-1 flex-col items-center justify-center py-12 text-center">
                <span className="rounded-2xl bg-violet-50 p-4 text-violet-700"><Sparkles className="h-7 w-7" /></span>
                <h3 className="mt-4 text-lg font-semibold text-slate-950">Připravit první návrh</h3>
                <p className="mt-2 max-w-md text-sm leading-6 text-slate-600">
                  AI použije jen podklad vlevo. Výsledek zůstane uložený jako návrh a nic se neodešle.
                </p>
                <button type="button" disabled={!hasEvidence || busy} onClick={handleGenerate} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50">
                  <Sparkles className="h-4 w-4" />
                  {operation === 'generate' ? 'Generuji…' : 'Vytvořit návrh'}
                </button>
              </div>
            ) : (
              <>
                {draft.status === 'stale' && (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Podklad se od vytvoření návrhu změnil. Tento text nelze uložit ani schválit; nejdřív ho znovu vygenerujte.
                  </div>
                )}

                <label className="text-sm font-medium text-slate-700" htmlFor="outreach-draft-subject">Předmět</label>
                <input
                  id="outreach-draft-subject"
                  value={subject}
                  maxLength={120}
                  disabled={busy || draft.status === 'stale' || draft.status === 'sent'}
                  onChange={(event) => setSubject(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-slate-100"
                />
                <div className="mt-1 text-right text-xs text-slate-400">{subject.length}/120</div>

                <label className="mt-4 text-sm font-medium text-slate-700" htmlFor="outreach-draft-body">Text e-mailu</label>
                <textarea
                  id="outreach-draft-body"
                  value={body}
                  maxLength={3000}
                  disabled={busy || draft.status === 'stale' || draft.status === 'sent'}
                  onChange={(event) => setBody(event.target.value)}
                  className="mt-2 min-h-[310px] w-full flex-1 resize-y rounded-xl border border-slate-300 px-3 py-3 text-sm leading-6 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-slate-100"
                />
                <div className="mt-1 text-right text-xs text-slate-400">{body.length}/3000</div>

                <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" disabled={!hasEvidence || busy || draft.status === 'sent'} onClick={handleGenerate} className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-200 px-4 py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50">
                    <RefreshCw className={`h-4 w-4 ${operation === 'generate' ? 'animate-spin' : ''}`} />
                    {operation === 'generate' ? 'Generuji…' : 'Vygenerovat znovu'}
                  </button>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button type="button" disabled={!dirty || !validContent || busy || draft.status === 'stale' || draft.status === 'sent'} onClick={handleSave} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                      <Save className="h-4 w-4" />
                      {operation === 'save' ? 'Ukládám…' : 'Uložit změny'}
                    </button>
                    <button type="button" disabled={dirty || !validContent || busy || draft.status === 'stale' || ['approved', 'sent'].includes(draft.status)} onClick={handleApprove} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
                      <Check className="h-4 w-4" />
                      {operation === 'approve' ? 'Schvaluji…' : ['approved', 'sent'].includes(draft.status) ? 'Schváleno' : 'Schválit návrh'}
                    </button>
                  </div>
                </div>

                <p className="mt-4 text-xs leading-5 text-slate-500">Schválení samo nic neodešle. Doručení vyžaduje samostatné finální potvrzení níže.</p>
                <DeliveryPanel contact={contact} onContactUpdated={updateContact} />
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  )
}

export default DraftWorkspace
